import { ReactNode } from "react";
import { color } from "src/styles/globals";
import styled, { css, createGlobalStyle } from "styled-components";
import Typography from "@sunday/component/lib/components/Typography";
import { Icon, Select, SelectProps } from "@sunday/component";

const { Body } = Typography;

export const AutoCompleteLabelText = styled(Body).attrs({
  designLevel: 2,
  weight: "bold",
})`
  --typography-color: var(--ui-form-input-select-activated-label_select-color);
  color: var(--ui-form-input-select-activated-label_select-color);
`;

const AutoCompleteErrorRow = styled(Body).attrs({
  designLevel: 2,
  weight: "regular",
})`
  &&& {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    --typography-color: var(
      --ui-form-input-select-error-helper_select-color,
      ${color.icthRed01}
    );
    color: var(
      --ui-form-input-select-error-helper_select-color,
      ${color.icthRed01}
    );
    [class*="luna-icon"] {
      flex-shrink: 0;
      color: var(
        --ui-form-input-select-error-helper_select-color,
        ${color.icthRed01}
      );
    }
  }
`;

export const AutoCompleteErrorText = ({
  children,
}: {
  children: ReactNode;
}) => (
  <AutoCompleteErrorRow>
    <Icon name="alert-circle-filled" size="20" />
    {children}
  </AutoCompleteErrorRow>
);

export const AutoCompleteHelperText = styled(Body).attrs({
  designLevel: 2,
  weight: "regular",
})`
  --typography-color: var(--ui-form-input-select-activated-helper_select-color);
  color: var(--ui-form-input-select-activated-helper_select-color);
`;

export const AutoCompleteErrorCss = css`
  ${AutoCompleteLabelText} {
    --typography-color: var(--ui-form-input-select-error-label_select-color);
    color: var(--ui-form-input-select-error-label_select-color);
  }

  .ant-select:not(.ant-select-disabled) {
    .ant-select-selector {
      background-color: var(--ui-form-input-select-error-background-color);
      border-color: var(
        --ui-form-input-select-error-border-color,
        ${color.icthRed01}
      ) !important;
    }

    .ant-select-selection-placeholder {
      color: var(--ui-form-input-select-error-placeholder_select-color);
    }
    .ant-select-arrow {
      color: var(--ui-form-input-select-error-icon-color);
    }
  }
`;

export const AutoCompleteDropdownGlobalStyle = createGlobalStyle`
  body .ant-select-dropdown,
  .ant-select-dropdown.ant-select-dropdown,
  .ant-select-dropdown.luna-autocomplete-popup,
  div[class*="luna-autocomplete-popup"],
  div[class*="luna-select-dropdown"],
  div[class*="dropdown"],
  .ant-select-dropdown-menu,
  .ant-select-item,
  .ant-select-item-option,
  .ant-select-item-option-content,
  .rc-virtual-list-holder-inner {
    font-family: "Noto Sans Thai", sans-serif;

    &,
    * {
      font-family: "Noto Sans Thai", sans-serif;
    }
  }

  body .ant-select-dropdown,
  .ant-select-dropdown.ant-select-dropdown,
  div[class*="luna-autocomplete-popup"],
  div[class*="luna-select-dropdown"],
  div[class*="dropdown"] {
    border-radius: 8px;
    overflow: hidden;

    .rc-virtual-list,
    .rc-virtual-list-holder,
    .rc-virtual-list-holder-inner {
      border-radius: 8px;
    }

    .ant-select-item {
      font-family: "Noto Sans Thai", sans-serif;
      font-weight: 400;

      .ant-select-item-option-content,
      .ant-select-item-option-content * {
        font-family: "Noto Sans Thai", sans-serif;
        font-size: 15px;
        line-height: 24px;
        font-weight: 400;
      }
    }
  }
`;

export const CustomSelect = styled(Select)`
  width: 100%;
  --select-font-weight: 400;
  --select-input-border-radius: 8px;

  &.ant-select {
    height: 48px;
    font-family: "Noto Sans Thai", sans-serif;
  }

  &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 48px !important;
    border-radius: 8px;
    padding: 0 16px;
    display: flex;
    align-items: center;

    .ant-select-selection-search {
      display: flex;
      align-items: center;
      inset-inline-start: 16px;
      inset-inline-end: 44px;
    }

    .ant-select-selection-search-input {
      height: 100%;
      font-family: "Noto Sans Thai", sans-serif;
      font-weight: 400;
    }

    .ant-select-selection-placeholder,
    .ant-select-selection-item {
      font-family: "Noto Sans Thai", sans-serif;
      font-size: 15px;
      line-height: 24px;
      font-weight: 400 !important;
    }

    .ant-select-selection-placeholder {
      font-weight: 400 !important;
      transition: none;
    }

    input {
      font-family: "Noto Sans Thai", sans-serif;
      font-weight: 400;
      &::placeholder {
        font-family: "Noto Sans Thai", sans-serif;
        font-weight: 400;
      }
    }
  }

  .ant-select-arrow {
    width: 24px;
    height: 24px;
    font-size: 24px;
    top: 50%;
    margin-top: -12px;
    inset-inline-end: 16px;

    [class*="luna-icon"],
    svg {
      width: 24px !important;
      height: 24px !important;
    }
  }
`;

const Container = styled.div.attrs(
  (props: { disabled: boolean; isError: boolean }) => props
)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  ${(props) =>
    !props.disabled &&
    !props.isError &&
    `:hover {
    ${AutoCompleteLabelText} {
      --typography-color: var(--ui-form-input-select-hover-label_select-color);
      color: var(--ui-form-input-select-hover-label_select-color);
    }
    ${AutoCompleteHelperText} {
      --typography-color: var(--ui-form-input-select-hover-helper_select-color);
      color: var(--ui-form-input-select-hover-helper_select-color);
    }
  }

  :focus-within {
    ${AutoCompleteLabelText} {
      --typography-color: var(--ui-form-input-select-focused-label_select-color);
      color: var(--ui-form-input-select-focused-label_select-color);
    }
    ${AutoCompleteHelperText} {
      --typography-color: var(--ui-form-input-select-focused-helper_select-color);
      color: var(--ui-form-input-select-focused-helper_select-color);
    }
  }`}

  ${(props) => props.isError && AutoCompleteErrorCss}

  .sunday-component-form-item-has-error &,
  .ant-form-item-has-error & {
    ${AutoCompleteErrorCss}
  }

  ${(props) =>
    props.disabled &&
    `
  ${AutoCompleteLabelText} {
    --typography-color: var(--ui-form-input-select-disabled-label_select-color);
    color: var(--ui-form-input-select-disabled-label_select-color);
  }
  ${AutoCompleteHelperText} {
    --typography-color: var(--ui-form-input-select-disabled-helper_select-color);
    color: var(--ui-form-input-select-disabled-helper_select-color);
  }
  `}
`;

export interface AutoCompleteFormProps extends Omit<SelectProps, "label"> {
  label?: ReactNode;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  dataTestId?: string;
}

const AutoCompleteForm = ({
  label,
  placeholder,
  helperText,
  errorText,
  disabled = false,
  isError = false,
  showSearch = true,
  dataTestId,
  dropdownStyle,
  ...props
}: AutoCompleteFormProps) => {
  return (
    <Container disabled={disabled} isError={isError}>
      <AutoCompleteDropdownGlobalStyle />
      {label && <AutoCompleteLabelText>{label}</AutoCompleteLabelText>}
      <CustomSelect
        showSearch={showSearch}
        placeholder={placeholder}
        disabled={disabled}
        status={isError ? "error" : undefined}
        data-testid={dataTestId}
        data-gtm-input-code={dataTestId}
        dropdownStyle={{
          borderRadius: 8,
          overflow: "hidden",
          ...dropdownStyle,
        }}
        {...props}
      />
      {isError && <AutoCompleteErrorText>{errorText}</AutoCompleteErrorText>}
      {helperText && (
        <AutoCompleteHelperText>{helperText}</AutoCompleteHelperText>
      )}
    </Container>
  );
};

export default AutoCompleteForm;
